import React, { useState, useRef } from 'react';
import Icon from '../AppIcon';

const FileUpload = ({ 
  label,
  description,
  accept = "image/*,.pdf,.doc,.docx",
  maxFiles = 5,
  maxFileSize = 10485760, // 10MB in bytes
  value = [],
  onChange,
  error,
  required = false,
  className = ""
}) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Detect mobile to show camera button
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  const supportsCamera = isMobile;

  const acceptedTypes = {
    'image/jpeg': { ext: 'jpg', icon: 'Image' },
    'image/jpg': { ext: 'jpg', icon: 'Image' },
    'image/png': { ext: 'png', icon: 'Image' },
    'image/gif': { ext: 'gif', icon: 'Image' },
    'application/pdf': { ext: 'pdf', icon: 'FileText' },
    'application/msword': { ext: 'doc', icon: 'FileText' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { ext: 'docx', icon: 'FileText' },
    'text/plain': { ext: 'txt', icon: 'FileText' }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  const validateFile = (file) => {
    if (!Object.keys(acceptedTypes)?.includes(file?.type)) {
      return `ประเภทไฟล์ "${file?.name}" ไม่ได้รับอนุญาต`;
    }
    if (file?.size > maxFileSize) {
      return `ไฟล์ "${file?.name}" มีขนาดเกิน ${formatFileSize(maxFileSize)}`;
    }
    return null;
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const currentFiles = value || [];

    if (currentFiles?.length + fileArray?.length > maxFiles) {
      alert(`สามารถอัพโหลดได้สูงสุด ${maxFiles} ไฟล์`);
      return;
    }

    const validFiles = [];
    const errors = [];

    fileArray?.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors?.push(error);
      } else {
        // Create file object with preview
        const fileObj = {
          id: Date.now() + Math.random(),
          file: file,
          name: file?.name,
          size: file?.size,
          type: file?.type,
          url: file?.type?.startsWith('image/') ? URL.createObjectURL(file) : null
        };
        validFiles?.push(fileObj);
      }
    });

    if (errors?.length > 0) {
      alert(errors?.join('\n'));
    }

    if (validFiles?.length > 0) {
      onChange([...currentFiles, ...validFiles]);
    }
  };

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === 'dragenter' || e?.type === 'dragover') {
      setDragActive(true);
    } else if (e?.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleFileInput = (e) => {
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const removeFile = (fileId) => {
    const newFiles = value?.filter(file => file?.id !== fileId);
    onChange(newFiles);
  };

  const openFileDialog = () => {
    fileInputRef?.current?.click();
  };

  const openCameraDialog = () => {
    const cameraInput = document.createElement('input');
    cameraInput.type = 'file';
    cameraInput.accept = 'image/*';
    cameraInput.capture = 'environment';
    cameraInput.onchange = (e) => {
      if (e?.target?.files && e?.target?.files?.[0]) {
        handleFiles(e?.target?.files);
      }
    };
    cameraInput.click();
  };

  const getFileIcon = (fileType) => {
    return acceptedTypes?.[fileType]?.icon || 'File';
  };

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Label */}
      <label className="block text-sm font-medium text-foreground">
        {label}
        {required && <span className="text-error ml-1">*</span>}
      </label>
      {/* Description */}
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {/* Upload Area */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6
          transition-all duration-200 cursor-pointer
          ${dragActive 
            ? 'border-primary bg-primary/5 scale-[1.02]' 
            : error 
              ? 'border-error hover:border-error/70' :'border-border hover:border-primary/50 hover:bg-accent/30'
          }
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={accept}
          onChange={handleFileInput}
          className="hidden"
        />

        <div className="text-center">
          <div className={`
            w-12 h-12 mx-auto mb-3 rounded-full flex items-center justify-center
            ${dragActive ? 'bg-primary text-white' : 'bg-accent text-muted-foreground'}
          `}>
            <Icon name={dragActive ? 'Upload' : 'Paperclip'} size={24} />
          </div>
          
          <p className="text-sm font-medium text-foreground mb-1">
            {dragActive ? 'วางไฟล์ที่นี่' : 'คลิกเพื่อเลือกไฟล์ หรือลากไฟล์มาที่นี่'}
          </p>
          
          <p className="text-xs text-muted-foreground">
            รองรับไฟล์: รูปภาพ, PDF, Word ({formatFileSize(maxFileSize)} สูงสุด {maxFiles} ไฟล์)
          </p>

          {supportsCamera && (
            <div className="mt-4">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openCameraDialog(); }}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
              >
                <Icon name="Camera" size={16} />
                <span>ถ่ายรูป</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* File List */}
      {value && value?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-foreground">
            ไฟล์ที่แนบ ({value?.length}/{maxFiles})
          </h4>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {value?.map((file, index) => (
              <div
                key={file?.id}
                className="flex items-center space-x-3 p-3 bg-accent/30 rounded-lg border border-border"
              >
                <div className="flex-shrink-0">
                  {file?.url ? (
                    <img
                      src={file?.url}
                      alt={file?.name}
                      className="w-10 h-10 object-cover rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-primary/10 rounded flex items-center justify-center">
                      <Icon name={getFileIcon(file?.type)} size={16} className="text-primary" />
                    </div>
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    {file?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(file?.size)}
                  </p>
                </div>

                <button
                  type="button"
                  onClick={(e) => {
                    e?.stopPropagation();
                    removeFile(file?.id);
                  }}
                  className="flex-shrink-0 p-1 text-muted-foreground hover:text-error transition-colors"
                  title="ลบไฟล์"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Error Message */}
      {error && (
        <p className="text-xs text-error">{error}</p>
      )}
    </div>
  );
};

export default FileUpload;